import { useCallback, useEffect, useRef, useState } from "react";
import { useScrollLock, useEscapeKey } from "../../hooks/useScrollLock";
import { useDispatch, useSelector } from "react-redux";
import { setModalOpen } from "../../store/Utilities";
import { RootState } from "../../store/store";

export default function Modal() {
    const { open, onConfirm, onReject, onClose, Chilren, width, hideModalFooter, showOneButton } = useSelector((state: RootState) => state.Utilities.modal);
    const dispatch = useDispatch()
    // Tracks an in-flight confirm handler so we can disable YES/Close and ignore
    // double-clicks while the (potentially async) handler is running.
    const [submitting, setSubmitting] = useState(false);
    // Modal is a singleton at the app root, so React state survives across opens.
    // The token guards against a stale handler from a prior modal (closed
    // externally mid-flight, e.g. via PunchListActionButtons unmount cleanup)
    // resolving later and stomping a fresh modal's submitting state.
    const inFlightToken = useRef(0);

    // Reset on close so a modal dismissed mid-submit (Cancel/Escape/external
    // setModalOpen(false)) doesn't leave the next modal's button disabled while
    // we wait for the prior handler to settle. Bumping the token here also
    // ensures the stale handler's finally won't re-toggle submitting later.
    useEffect(() => {
        if (!open) {
            inFlightToken.current++;
            setSubmitting(false);
        }
    }, [open]);

    const closeModal = useCallback(() => {
        onClose?.();
        dispatch(setModalOpen(false));
    }, [onClose])

    const onConfirmModal = async () => {
        if (submitting) return; // guard against double-click / re-entrance
        const myToken = ++inFlightToken.current;
        setSubmitting(true);
        try {
            const result = await onConfirm?.();
            // Handlers can return `false` to keep the modal open (e.g. validation failed).
            if (result !== false) closeModal();
        } catch (err) {
            // Fail-open so the user is never stranded on a stuck modal. The handler
            // is expected to surface its own error UI (toast); this is a safety net.
            console.error('Modal confirm handler threw:', err);
            closeModal();
        } finally {
            if (myToken === inFlightToken.current) setSubmitting(false);
        }
    }

    const onRejectModal = () => {
        onReject?.();
        closeModal();
    }

    useEscapeKey(open, closeModal);
    useScrollLock(open);

    return (
        <div
            onClick={closeModal}
            role="dialog"
            aria-modal={open}
            className={`fixed inset-0 flex justify-center items-center transition-colors z-10
            ${open ? "visible bg-black/20" : "invisible"}`}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`rounded-xl shadow p-4 transition-all bg-white text-black dark:bg-black dark:text-white flex gap-2
                ${open ? "scale-100 opacity-100" : "scale-125 opacity-0"} ${width ?? ''}`}
            >

                <div className="mt-4 p-2">
                    <main>{Chilren && <Chilren />}</main>
                    {!hideModalFooter &&
                        <div className="flex mt-6 justify-between">
                            <button
                                className="bg-orange-1000 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                                onClick={onConfirmModal}
                                disabled={submitting}
                                aria-busy={submitting || undefined}
                            >
                                {submitting ? 'Working…' : 'YES'}
                            </button>
                            <button className="bg-gray-200 text-black" onClick={onRejectModal}>CANCEL</button>
                        </div>}
                    {showOneButton &&
                        <div className="flex mt-6 justify-center">
                            <button
                                className="bg-orange-1000 text-white w-full disabled:opacity-60 disabled:cursor-not-allowed"
                                onClick={onConfirmModal}
                                disabled={submitting}
                                aria-busy={submitting || undefined}
                            >
                                {submitting ? 'Working…' : 'Close'}
                            </button>
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}
